import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { Button, Card, CardHeader, CardContent, Input, Field, FieldLabel, FieldError } from '../../components/ui';
import { passwordSchema, type PasswordFormData } from './settingsSchemas';

export default function SettingsPasswordTab() {
  const [loading, setLoading] = React.useState(false);

  const form = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const handleSubmit = async () => {
    try {
      setLoading(true);
      // TODO: Call API to update password when backend supports it
      toast.success('Password updated successfully!');
      form.reset();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Security Settings
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Update your password to keep your account secure
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <Field data-invalid={!!form.formState.errors.currentPassword}>
            <FieldLabel htmlFor="currentPassword">Current Password *</FieldLabel>
            <Input
              id="currentPassword"
              type="password"
              {...form.register('currentPassword')}
              placeholder="Enter current password"
              aria-invalid={!!form.formState.errors.currentPassword}
            />
            <FieldError>{form.formState.errors.currentPassword?.message}</FieldError>
          </Field>

          <Field data-invalid={!!form.formState.errors.newPassword}>
            <FieldLabel htmlFor="newPassword">New Password *</FieldLabel>
            <Input
              id="newPassword"
              type="password"
              {...form.register('newPassword')}
              placeholder="Enter new password"
              aria-invalid={!!form.formState.errors.newPassword}
            />
            <FieldError>{form.formState.errors.newPassword?.message}</FieldError>
          </Field>

          <Field data-invalid={!!form.formState.errors.confirmPassword}>
            <FieldLabel htmlFor="confirmPassword">Confirm New Password *</FieldLabel>
            <Input
              id="confirmPassword"
              type="password"
              {...form.register('confirmPassword')}
              placeholder="Confirm new password"
              aria-invalid={!!form.formState.errors.confirmPassword}
            />
            <FieldError>{form.formState.errors.confirmPassword?.message}</FieldError>
          </Field>

          <div className="flex justify-end">
            <Button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
